import React, { useContext, useState } from 'react';
import { Form, Input, Divider, Checkbox } from 'antd';
import UiButton from '@components/ui/button/UiButton';
import makeTypedTranslation from '@hooks/typedTranslations/makeTypedTranslation';
import Validation from '@components/ka-ui/validation/Validation';
import { lengthValidator, lettersValidator, numbersValidator, specialCharactersValidator } from '@utils/password';
import { useRouter } from 'next/router';
import AuthCard from '@components/ka-ui/auth-card/AuthCard';
import { EMAIL_REGEX } from '@utils/regex';
import { signup } from '@services/auth/auth-http-service';
import { SignUpDto } from '@services/auth/type';
import ErrorMessagesEnum from '@constants/error-messages.enum';
import RoutesEnum from '@constants/routes.enum';
import { AuthLayoutContext, AuthLayoutErrorTypes } from '@contexts/AuthLayoutContext';
import { AuthContext } from '@contexts/AuthContext';
import { UserTypeEnum } from '@models/user/auth';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { Trans } from 'next-i18next';
import createGtagEvent from '@utils/create-gtag-event';
import SignUpHeader from '@components/sign-up/sign-up-header/SignUpHeader';
import AlreadyHaveAnAccount from '../common/already-have-an-account/AlreadyHaveAnAccount';
import styles from './SignUp.module.scss';

const TERMS_AND_CONDITIONS_URL = `${process.env.NEXT_PUBLIC_HRMS_MARKETING_URL}/terms-and-conditions`;
const PRIVACY_POLICY_URL = `${process.env.NEXT_PUBLIC_HRMS_MARKETING_URL}/data-protection-policy`;

const SignUp: React.FC = () => {
  const [form] = Form.useForm();
  const router = useRouter();
  const { useTypedTranslation } = makeTypedTranslation('signup', 'forms', 'notification', 'common');

  const { setErrorMessage } = useContext(AuthLayoutContext);
  const { setUser } = useContext(AuthContext);

  const { t: translate } = useTypedTranslation('signup');
  const { t: formsTranslate } = useTypedTranslation('forms');
  const { t: commonTranslate } = useTypedTranslation('common');

  const [formValid, setFormValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [termsChecked, setTermsChecked] = useState<boolean>(false);

  const [passwordValidations, setPasswordValidations] = useState<{
    isEightCharacters?: boolean;
    hasNumbers?: boolean;
    hasLetters?: boolean;
    hasSpecialCharacters?: boolean;
  }>({
    isEightCharacters: undefined,
    hasNumbers: undefined,
    hasLetters: undefined,
    hasSpecialCharacters: undefined,
  });

  const handleFormChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    setFormValid(!hasErrors && form.isFieldsTouched(true));
  };

  const onFinish = async (data: SignUpDto) => {
    setSubmitting(true);
    try {
      await signup(data);
      setUser({ type: UserTypeEnum.EMPLOYER });
      createGtagEvent('SignUp_Success', 'SignUp', 'Click on "Continue" Button on SignUp page to create a new account', {
        email: data.email,
        fullName: `${data.first_name} ${data.last_name}`,
      });

      router.push(RoutesEnum.ADMIN_ACCOUNT_SETUP).then(() => {
        setSubmitting(false);
        setErrorMessage(undefined);
      });
    } catch (error: any) {
      setSubmitting(false);
      if (error?.message === ErrorMessagesEnum.EMAIL_EXISTS) {
        setErrorMessage(AuthLayoutErrorTypes.EMAIL_EXISTS);
        return;
      }
      setErrorMessage(AuthLayoutErrorTypes.GENERIC_ERROR);
    }
  };

  /**
   * Validates the given password.
   */
  const passwordValidator = () => ({
    async validator(_: any, value: string) {
      const isEightCharacters = lengthValidator(value);
      const hasLetters = lettersValidator(value);
      const hasNumbers = numbersValidator(value);
      const hasSpecialCharacters = specialCharactersValidator(value);

      setPasswordValidations({
        isEightCharacters,
        hasNumbers,
        hasLetters,
        hasSpecialCharacters,
      });

      if (isEightCharacters && hasNumbers && hasLetters && hasSpecialCharacters) {
        return;
      }
      throw new Error('error');
    },
  });

  const handleTermsCheckboxChange = (event: CheckboxChangeEvent) => {
    const { checked } = event.target;
    setTermsChecked(checked);
  };

  return (
    <AuthCard header={<SignUpHeader />}>
      <style>{'.grecaptcha-badge{visibility: visible;}'}</style>
      <section>
        <h6 className={`h6 uppercase subheading ${styles.stepTitle}`}>
          {commonTranslate('authLayout.sign_up_step', { stepNumber: 1 })}
        </h6>
        <Form
          form={form}
          layout="vertical"
          size="small"
          data-testid="signup-form"
          onFieldsChange={handleFormChange}
          onFinish={onFinish}
          autoComplete="off"
          className={styles.signUpForm}
        >
          <div className={styles.adminNameWrap}>
            <Form.Item
              label={translate('first_name')}
              required
              name="first_name"
              rules={[
                {
                  required: true,
                  message: formsTranslate('required_minimal'),
                },
              ]}
            >
              <Input data-testid="first-name" placeholder={translate('first_name_placeholder')} size="large" />
            </Form.Item>
            <Form.Item
              label={translate('last_name')}
              required
              name="last_name"
              rules={[
                {
                  required: true,
                  message: formsTranslate('required_minimal'),
                },
              ]}
            >
              <Input data-testid="last-name" placeholder={translate('last_name_placeholder')} size="large" />
            </Form.Item>
          </div>
          <Form.Item
            label={translate('email')}
            name="email"
            required
            rules={[
              {
                required: true,
                message: formsTranslate('email_required'),
              },
              {
                pattern: EMAIL_REGEX,
                message: formsTranslate('not_valid_email'),
              },
              {
                max: 255,
                message: formsTranslate('max_255_chars_error'),
              },
            ]}
          >
            <Input data-testid="signup-email" placeholder={translate('email_placeholder')} size="large" />
          </Form.Item>
          <Form.Item
            label={translate('password')}
            name="password"
            required
            className={styles.passwordFormItem}
            rules={[
              {
                required: true,
                message: formsTranslate('required_minimal'),
              },
              passwordValidator,
            ]}
          >
            <Input.Password data-testid="password" placeholder={translate('password_placeholder')} size="large" />
          </Form.Item>
          <div className={styles.validationInfo}>
            <Validation text={translate('validations.min_required')} valid={passwordValidations.isEightCharacters} />
            <Validation text={translate('validations.letters_required')} valid={passwordValidations.hasLetters} />
            <Validation text={translate('validations.numbers_required')} valid={passwordValidations.hasNumbers} />
            <Validation
              text={translate('validations.special_char_required')}
              valid={passwordValidations.hasSpecialCharacters}
            />
          </div>
          <Form.Item name="accepted_terms" valuePropName="checked">
            <Checkbox data-testid="terms-checkbox" onChange={handleTermsCheckboxChange}>
              <Trans t={translate} i18nKey="terms_info">
                _
                <a href={TERMS_AND_CONDITIONS_URL} className="main-body-text">
                  _
                </a>
                <a href={PRIVACY_POLICY_URL} className="main-body-text">
                  _
                </a>
              </Trans>
            </Checkbox>
          </Form.Item>
          <UiButton
            type="primary"
            size="middle"
            htmlType="submit"
            data-testid="submit-button"
            loading={submitting}
            className={styles.submitButton}
            disabled={!formValid || !termsChecked}
          >
            {translate('continue')}
          </UiButton>
        </Form>
      </section>
      <Divider className={styles.divider} />
      <AlreadyHaveAnAccount onClick={() => router.push(RoutesEnum.LOGIN)} />
    </AuthCard>
  );
};

export default SignUp;
